import { BlueprintFamilyTrendsWindowDays } from '../../../../api/core'

// Every Live Event Analytics figure covers the selected period: the last N complete
// calendar months (UTC), ending with the month before the one in progress. The month in
// progress is never part of it, even on its last day, so the period moves only on the 1st.
//
// The trends endpoints return exactly the period's months, so on the current contract
// `selectedPeriodAxis` keeps every month the response carries. It stays as a guard so a
// response from an older backend (which also returned the previous period and the month
// in progress) still plots the selected period only.

// Months a `windowDays` span covers, on the 30-day month the backend's window sizes are
// built from: 180 → 6, 360 → 12, 720 → 24.
export function windowMonths(windowDays: number): number {
  return Math.round(windowDays / 30)
}

// "yyyy-MM" shifted by `delta` calendar months.
export function addMonths(month: string, delta: number): string {
  const [year, monthIndex] = month.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, monthIndex - 1 + delta, 1))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
}

// The selected period's calendar months. Months are "yyyy-MM".
export interface TrendsSelectedPeriod {
  // First month of the period: N months before the in-progress one. It is also the
  // baseline the adoption Change compares against (see `momentum.ts`).
  startMonth: string
  // The month in progress. The period ends with the month before it.
  inProgressMonth: string
}

// The selected period's calendar months, or null when there is no window to plot.
// Resolved in UTC, as the backend does: a local-time month would be one off for a
// viewer whose offset straddles a month boundary.
export function selectedPeriodFor(windowDays: BlueprintFamilyTrendsWindowDays | undefined, today: Date = new Date()): TrendsSelectedPeriod | null {
  if (windowDays === undefined) return null
  const inProgressMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`
  return {
    startMonth: addMonths(inProgressMonth, -windowMonths(windowDays)),
    inProgressMonth,
  }
}

// The months to plot for the selected period: from `startMonth` up to but excluding the
// in-progress month. Months the response does not cover are simply absent — nothing is
// padded.
export function selectedPeriodAxis(months: readonly string[], period: TrendsSelectedPeriod): string[] {
  return months.filter((month) => month >= period.startMonth && month < period.inProgressMonth)
}
